// @writing-suite/core — the Revise room's logic (W7c), ported from SuiteModel, SuiteStyle and Apps/Shared/Rooms:
// common mistakes (dashes, ellipses, capitals, dialogue marks), the style linter, reading marks and findings, revision
// mode (RevisionDiff, levels, accepted patches), revision heat and cold scenes, and the typed writes behind them.
// Quotation marks, quotations, dialogue balance, readability, sentence lengths and word frequency are the shared text
// primitives (`../text`, the core's `text` namespace).
//
// Exact unless marked: sentence boundaries (Intl.Segmenter for NLTokenizer) and everything that needs NLTagger (repeated
// words, crowded describing words, word frequency's grouping) — see text/sentences.ts and text/tagger.ts.
// Personal style rules are Apple-only (style/personal.ts).

// Common mistakes and dashes.
export {
  DASH_STYLES, dashStyleName, dashStyleExample, dashOf, isSpaced, usualFromCensus, projectDashStyle, type DashStyle, type DashCensus,
} from './dashStyle.ts';
export {
  TextCleanup, DashStyleCensus, CLEANUP_CHECKS, STANDARD_CLEANUP_CHECKS, cleanupCheckPass, REVISE_PASSES, revisePassName, revisePassSummary,
  type RevisePass, cleanupCheckName, cleanupCheckSummary, checkText, fixedText, surveyTexts, surveyTotal,
  fixableCount, toReview, isClean, dashList, dashes, dashCensus, usualDashStyle, listMarkers, spanClose, isSpeech, ABBREVIATIONS,
  type CleanupCheck, type CleanupFinding, type CleanupOptions, type CleanupReport, type CleanupSurvey, type Dash,
} from './textCleanup.ts';
export {
  projectStyleSheet, styleSheetMarkdown, NUMBER_STYLES, numberStyleName, DIALOGUE_QUOTES_KEY, NUMBERS_KEY, type StyleSheet, type NumberStyle,
} from './styleSheet.ts';

// Consistency: a word written two ways in the book.
export {
  VARIANT_KINDS, variantKindName, variantKindSummary, variantChoosesAForm, flaggedOccurrences, makeChoice, variantKey, choiceKey, choiceID, choiceJSON,
  findVariants, readStyleChoices, spelledOut, CHOICE_PREFIX, type VariantKind, type VariantOccurrence, type VariantSet, type VariantChoice,
  type VariantOptions,
} from './variants.ts';

// The style linter.
export {
  BUILT_IN_STYLE_CHECKS, styleCheckPass, styleCheckName, styleCheckSummary, styleCheckIsExact, checkStyle, styleText, styleTexts, blankingSpanClasses,
  splitParagraphs, sentenceAround, stableID, ruleCheck, styleSettings, STYLE_COMMON,
  type BuiltInStyleCheck, type StyleFinding, type StyleText, type StyleRule, type StyleSettings, type StyleParagraph,
} from './style/linter.ts';
export { CRUTCHES, overusedWords, type Crutch } from './style/overused.ts';
export { scanPersonalRule, PERSONAL_RULES_APPLE_ONLY, type PersonalRuleScan } from './style/personal.ts';

// Comment categories.
export {
  categoryInk, categoryNamed, categoryOf, commentCategory, commentCategoryID, commentCategoryKey, decodeCommentCategory, nextCategoryInk,
  normalizedCategoryName, readCommentCategories, COMMENT_CATEGORY_NAME_LIMIT, COMMENT_CATEGORY_PREFIX, type CommentCategory,
} from './commentCategories.ts';

// Link comments (importers' links kept as margin comments) turned into links.
export { linkCommentAddress, linkCommentCandidates, linkCommentCount, linkedComments, type LinkCommentCandidate } from './linkComments.ts';

// Reading marks and findings.
export { ReadingMarks, resolvePassage, readingParagraphs, type ReadingMark, type ReadingParagraph, type ReadingParagraphKind } from './readingMarks.ts';
export {
  reviseUnits, reviseUnit, reviseScenes, reviseFindings, reviseBook, findingPreview, findingContext, findingPass, findingPrefixes, isFixable,
  isRuleFinding, variantMessage, spellingFindings, marginCommentFor, styleComment, continuityComment, STYLE_LINTER_AUTHOR, CONTINUITY_AUTHOR,
  type ReviseFinding, type ReviseFindingOptions, type ReviseBook, type ReviseBookOptions, type ReviseScene,
} from './findings.ts';
export {
  QUOTES_CHECK, SPELLING_CHECK, MISTAKE_CHECKS, TYPOGRAPHY_CHECKS, CONSISTENCY_CHECKS, STYLE_CHECKS, COLUMN_PASSES, OFF_BY_DEFAULT, checksIn,
  checkPass, checkName, checkShortName, checkSummary, fixesAllAtOnce, isCheckOn, isBuiltInStyleCheck, type ReviseSwitches,
} from './checks.ts';

// Revision mode.
export {
  RevisionDiff, revisionChanges, revisionTokens, readRevisions, decodeRevisions, encodeRevisions, revisionsJSON, emptyRevisions,
  isRevisionActive, currentRevision, applyPatches, patchApplies, revisionBase, acceptPatch, changeID, changeAt, nextChange, previousChange,
  rejectReplacement, revisionTexts, fragmentRevisionChanges, revisionCounts, changesIn, changeFits, storedText,
  type Revision, type RevisionPatch, type Revisions, type RevisionChange, type RevisionTexts,
} from './revisions.ts';

// Revision heat and cold scenes.
export {
  heatLevels, heatByNode, isCold, isColdNode, rewriteVolume, heatSince, revisionHeat, nodeHeat, heatmapPeriodName, HEATMAP_PERIODS,
  DEFAULT_COLD_DAYS,
} from './heat.ts';

// Spelling on the web: a Hunspell reader over SCOWL's dictionaries, and the words to check.
export { Speller, spellingDictionary, type SpellingDictionary } from './speller.ts';
export { spellingCandidates, SPELLING_MOST_USES, type SpellingWord } from './spellingCandidates.ts';

// Writes.
export { ReviseEditor, type PassageFix } from './edits.ts';
